import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UserModel } from "app/core/models/user.model";
import { environment } from "environments/environment";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { finalize, map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private unsubscribe: Subscription[] = [];
  public isSwitchingAccount = false;
  //for spinner
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;
  //for store user detail
  currentUserSubject: BehaviorSubject<any>;
  currentUser$: Observable<any>;
  private apiUrl = environment.apiUrl;

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: any) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  login(data) {
    this.isLoadingSubject.next(true);

    return this.http.post(`${this.apiUrl}/user/login`, data).pipe(
      map((auth: any) => {
        auth.result.profile_picture = auth.result.profile_picture;
        auth.result.channel_status = auth.channel_status;
        auth.result.contractCount = auth.contractCount;
        auth.result.login_id = auth?.login_id;
        auth.result.team_member_type = auth.team_member_type;
        auth.result.label_data = auth.label_data;
        auth.result.stripe_price_id = auth.result?.stripe_price_id;
        auth.result.user_main_data = auth.user_main_data;
        if (auth.result.type == "8") {
          if (auth.team_member_type == "creator") {
            auth.result.type = 9;
          }
        }
        const result = this.setStorage(auth);
        this.currentUserSubject.next(auth.result);
        return auth;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  checktokenisvalid(data) {
    return this.http.post(`${this.apiUrl}/user/verify/token`, data).pipe(
      map((auth: any) => {
        return auth;
      })
    );
  }

  forgotpassword(data) {
    this.isLoadingSubject.next(true);
    return this.http.post(`${this.apiUrl}/user/reset-password`, data).pipe(
      map((auth: any) => {
        return auth;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  resetpassword(data) {
    this.isLoadingSubject.next(true);
    return this.http.post(`${this.apiUrl}/user/change/password`, data).pipe(
      map((auth: any) => {
        return auth;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  getUserByToken(): Observable<UserModel> {
    const token = this.getStorage();

    if (!token) {
      return of(undefined);
    } else {
      this.isLoadingSubject.next(true);
      return this.http.patch(`${this.apiUrl}/user`, {}).pipe(
        map((user: any) => {
          if (user) {
            const userDetail = user?.result;
            userDetail.channel_status = user?.channel_status;
            userDetail.contractCount = user?.contractCount;
            userDetail.login_id = user?.login_id;
            userDetail.team_member_type = user?.team_member_type;
            userDetail.label_data = user?.label_data;
            userDetail.user_main_data = user?.user_main_data;
            userDetail.theme = userDetail.theme ? userDetail.theme : "dark";
            if (userDetail.type == "8") {
              if (user?.team_member_type == "creator") {
                userDetail.type = 9;
              }
            }
     
            if (userDetail.type == "4") {
              if (userDetail?.onboarding_status < 5) {
                this.currentUserSubject.next(userDetail);
                this.router.navigate(["/onboarding"]);
              } else {
                this.currentUserSubject.next(userDetail);
              }
            } else {
              this.currentUserSubject.next(userDetail);
            }
          } else {
            this.logout();
          }
          return user;
        }),
        finalize(() => this.isLoadingSubject.next(false))
      );
    }
  }

  currentusertype() {
    const user = this.currentUserValue;
    if (user.type == 1) {
      return "admin";
    }
    if (user.type == 2) {
      return "staff";
    }
    if (user.type == 3) {
      return "creator";
    }
    if (user.type == 4) {
      return "right_holder";
    }
    if (user.type == 5) {
      return "manager_limited";
    }
    if (user.type == 6) {
      return "viewer";
    }
    if (user.type == 7) {
      return "viewer_limitd";
    }

    if (user.type == 10) {
      return "true_super_content_owner";
    }
  }

  isAdminCategory() {
    const admins = [1, 2, 5, 6, 7];
    return admins.includes(this.currentUserValue?.type);
  }

  isCRUDAdmin() {
    const admins = [1, 2, 5];
    return admins.includes(this.currentUserValue?.type);
  }

  isNonCRUDAdmin() {
    const admins = [6, 7];
    return admins.includes(this.currentUserValue?.type);
  }

  isEarningAdmin() {
    const admins = [1, 2, 6];
    return admins.includes(this.currentUserValue?.type);
  }

  isNonEarningAdmin() {
    const admins = [5, 7, 8, 9];
    return admins.includes(this.currentUserValue?.type);
  }

   isManagerLimited() {
    const admins = [5, 7];
    return admins.includes(this.currentUserValue?.type);
  }

  isCreator() {
    const admins = [3];
    return admins.includes(this.currentUserValue?.type);
  }

  isRH() {
    const admins = [4];
    return admins.includes(this.currentUserValue?.type);
  }

  isRHTeam() {
    const admins = [8];
    return admins.includes(this.currentUserValue?.type);
  }

  isCreatorTeam() {
    const admins = [9];
    return admins.includes(this.currentUserValue?.type);
  }

  isTrueSuperContentOwner() {
    const admins = [10];
    return admins.includes(this.currentUserValue?.type);
  }

  updateChannelStatus(status: number): void {
    const current = this.currentUserSubject.value;
    if (current) {
      const updatedUser = { ...current, channel_status: status };
      this.currentUserSubject.next(updatedUser);
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }
  }

  private getStorage(): any {
    try {
      const authData = localStorage.getItem("token");
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("uploadMenu");
    localStorage.removeItem("content_owner_array");
    this.currentUserSubject.next(undefined);
    this.router.navigate(["/login"]);
  }

  setStorage(auth: any) {
    if (auth?.token) {
      localStorage.setItem("token", auth.token);
      localStorage.setItem("user_id", auth.result._id);
    }
  }

  clearStorage() {
    localStorage.removeItem("token");
    this.currentUserSubject.next(undefined);
  }
}
